import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { overlayDismiss } from "./overlayDismiss";

// Accessible, promise-based confirm/prompt — a replacement for the synchronous,
// unstyled window.confirm() / window.prompt(). Mount <ConfirmProvider> once near
// the app root; call the resolved function from useConfirm() and `await` it.
//
//   const confirm = useConfirm();
//   if (await confirm({ message: "Delete?", danger: true })) …           // boolean
//   const text = await confirm({ message: "Why?", prompt: true });       // string | null (null = cancelled)
//
// The dialog traps focus (Tab wraps within it), restores focus to the previously
// focused element on close, closes on Escape (cancel) and confirms on Enter.

export interface ConfirmOptions {
  message: string;
  // Rich body rendered in place of `message` (e.g. a page title in bold). `message`
  // stays required as the plain-text version — it is the prompt input's label.
  messageNode?: ReactNode;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean; // style the confirm button as destructive
  // When set, the dialog shows a text input and resolves to the entered string
  // (or null if cancelled) instead of a boolean.
  prompt?: boolean;
  placeholder?: string;
  defaultValue?: string;
  // Render the prompt's input as a multi-line textarea instead of a single-line
  // <input> — for notes/comments where a one-liner is too cramped. Only
  // meaningful together with `prompt: true`. Plain Enter inserts a newline in
  // this mode (⌘/Ctrl+Enter confirms instead — see the keydown handler below).
  multiline?: boolean;
  // Make the prompt's answer mandatory: the string is the validation message
  // shown under the field, and confirming is blocked (button disabled, Enter
  // inert) while the value is empty or whitespace. Cancel still resolves null,
  // so a required prompt is refusable — it just can't be answered with nothing.
  // Only meaningful together with `prompt: true`.
  requireValue?: string;
}

// The promise resolves to a boolean for a plain confirm, or string|null for a prompt.
type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean | string | null>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface Pending {
  opts: ConfirmOptions;
  resolve: (value: boolean | string | null) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise((resolve) => setPending({ opts, resolve }));
  }, []);

  const close = useCallback(
    (value: boolean | string | null) => {
      setPending((cur) => {
        cur?.resolve(value);
        return null;
      });
    },
    [],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <Dialog pending={pending} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

function Dialog({ pending, onClose }: { pending: Pending; onClose: (value: boolean | string | null) => void }) {
  const { opts } = pending;
  const isPrompt = !!opts.prompt;
  const isMultiline = isPrompt && !!opts.multiline;
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const [value, setValue] = useState(opts.defaultValue ?? "");

  // The cancel resolution differs by mode: false for a confirm, null for a prompt.
  const cancelValue = isPrompt ? null : false;
  const confirmValue = isPrompt ? value : true;
  // A required prompt answered with nothing (or only whitespace) can't confirm.
  const blocked = isPrompt && !!opts.requireValue && value.trim() === "";

  // Restore focus to whatever was focused before the dialog opened.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Focus the input/textarea (prompt) or the confirm button on open.
    (isPrompt ? (isMultiline ? textareaRef.current : inputRef.current) : confirmBtnRef.current)?.focus();
    return () => previouslyFocused?.focus?.();
  }, [isPrompt, isMultiline]);

  // Keyboard: Escape cancels; Enter confirms — EXCEPT in the multiline textarea,
  // where a plain Enter must insert a newline (its native behavior); there,
  // ⌘/Ctrl+Enter confirms instead. Tab is trapped within the dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose(cancelValue);
        return;
      }
      if (e.key === "Enter") {
        const inTextarea = (e.target as HTMLElement | null)?.tagName === "TEXTAREA";
        if (inTextarea && !(e.metaKey || e.ctrlKey)) return; // let the newline through
        e.preventDefault();
        if (blocked) return; // required and empty — same gate as the disabled button
        onClose(confirmValue);
        return;
      }
      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const focusable = root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, cancelValue, confirmValue, blocked]);

  return (
    <div className="modal-overlay open" {...overlayDismiss(() => onClose(cancelValue))}>
      <div
        ref={dialogRef}
        className="modal modal-confirm"
        role="dialog"
        aria-modal="true"
        aria-label={opts.title ?? "Confirm"}
        onClick={(e) => e.stopPropagation()}
      >
        {opts.title && <div className="modal-title">{opts.title}</div>}
        <div className="modal-text">{opts.messageNode ?? opts.message}</div>
        {isPrompt && (isMultiline ? (
          <textarea
            ref={textareaRef}
            className="modal-input modal-textarea"
            rows={4}
            value={value}
            placeholder={opts.placeholder}
            onChange={(e) => setValue(e.target.value)}
            aria-label={opts.message}
            aria-invalid={blocked || undefined}
            aria-describedby={blocked ? "modal-required" : undefined}
          />
        ) : (
          <input
            ref={inputRef}
            className="modal-input"
            value={value}
            placeholder={opts.placeholder}
            onChange={(e) => setValue(e.target.value)}
            aria-label={opts.message}
            aria-invalid={blocked || undefined}
            aria-describedby={blocked ? "modal-required" : undefined}
          />
        ))}
        {/* The requirement is stated up front rather than only after a failed
            attempt — Save is disabled while the field is empty, so there is no
            attempt to fail. role="status" so it isn't announced as an error the
            user has already made. */}
        {blocked && <div className="modal-required" id="modal-required" role="status">{opts.requireValue}</div>}
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => onClose(cancelValue)}>
            {opts.cancelLabel ?? "Cancel"}
          </button>
          <button
            ref={confirmBtnRef}
            className={opts.danger ? "btn-danger" : "btn-primary"}
            disabled={blocked}
            onClick={() => onClose(confirmValue)}
          >
            {opts.confirmLabel ?? "OK"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Typed accessors. `useConfirm` resolves boolean; `usePrompt` resolves string|null.
export function useConfirm(): (opts: Omit<ConfirmOptions, "prompt">) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within a ConfirmProvider");
  return useMemo(() => (opts) => ctx({ ...opts, prompt: false }) as Promise<boolean>, [ctx]);
}

export function usePrompt(): (opts: Omit<ConfirmOptions, "prompt">) => Promise<string | null> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("usePrompt must be used within a ConfirmProvider");
  return useMemo(() => (opts) => ctx({ ...opts, prompt: true }) as Promise<string | null>, [ctx]);
}
